import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box } from '@material-ui/core';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import EmptyAnswer from 'component/StandardAlert/no_answer';
import { User } from 'component/stylist_profile_header/types';
import { useAuth } from 'contexts/AuthContext';
import { GET_USER_BY_USERNAME } from 'graph_ql/users/queries';
import { useEffect, useMemo, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setHairColorDefault } from 'redux/authentication/user_detail';
import { EQuestionId, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { useGetGender } from '../../../hooks/useGetGender';
import { PATH } from '../../../routes/constant';
import { HairColorItem } from './HairColorItem';
import { useStyles } from './styles';
import useValidateAnswers from '../hooks/useValidateAnswers';
export interface IHairColorProps {}

const HairColor = (props: { hideTitle?: boolean }) => {
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const history = useHistory();
    const gender = useGetGender(Boolean(userName));
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [quizData, setQuizData] = useState<any>(null);
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [user, setUser] = useState<User>();
    const [defaultValueHairColor, setDefaultValueHairColor] = useState<any>(null);
    const [isSkipping, setSkipping] = useState<boolean>(false);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);

    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setDefaultValueHairColor(data.getQuizResponse?.answers?.hair_color_question);
            setQuizData(data.getQuizResponse);
        },
    });
    const [getUserByUserName, { loading: userLoading }] = useLazyQuery(GET_USER_BY_USERNAME, {
        onCompleted(data) {
            setUser(data.getUserByUserName);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.HAIR_COLOR,
                        contextType: quizContextType,
                        contextId: EQuizId.HAIR_COLOR,
                        userId: data.getUserByUserName?.id,
                    },
                },
            });
        },
    });

    const createAnswer = async (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: String;
        valueType: String;
        isSkipped?: boolean;
    }) => {
        const variables = {
            input: {
                ...input,
                ...(defaultValueHairColor ? { id: defaultValueHairColor.id } : {}),
            },
        };
        await answerMutation({ variables });
    };
    const [answerMutation, { loading: loadingAnswer }] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            onAnswerCompleted(response);
            dispatch(setHairColorDefault(response.createAnswer));
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const onAnswerCompleted = async (response: any) => {
        await getQuizResponse({
            variables: {
                input: {
                    quizId: EQuizId.HAIR_COLOR,
                    contextType: quizContextType,
                    contextId: EQuizId.HAIR_COLOR,
                    userId: currentUser.uid || gender?.id,
                },
            },
        });
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    };
    const { data: hairColorData, loading } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: ESubjectQuestion.HAIR_COLOR,
                paging: { page: 0, size: 0 },
                enabled: true,
            },
        },
        skip: Boolean(!userName) ? !gender : !user,
    });

    const listHairColors = useMemo(
        () => (hairColorData?.getQuestionData?.data || []).map((item) => ({ ...item, isSelected: false })),
        [hairColorData?.getQuestionData?.data],
    );

    const [listHairColorOptions, setListHairColorOptions] = useState(listHairColors || []);

    useEffect(() => {
        if ((currentUser && Boolean(!userName)) || gender) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.HAIR_COLOR,
                        contextType: quizContextType,
                        contextId: EQuizId.HAIR_COLOR,
                        userId: currentUser.uid || gender?.id,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.uid, gender, userName]);

    useEffect(() => {
        if (userName) {
            getUserByUserName({
                variables: {
                    input: {
                        userName,
                    },
                },
            });
        }
    }, [userName, getUserByUserName]);

    useEffect(() => {
        let listHairColorsCurrent: any = [];
        if (listHairColors && listHairColors.length > 0 && !loadingQuiz) {
            listHairColorsCurrent = [...listHairColors];
            const defaultValueIndex = listHairColorsCurrent.findIndex(
                (item) => item.value.toLowerCase() === defaultValueHairColor?.value?.toLowerCase(),
            );
            if (defaultValueIndex > -1) listHairColorsCurrent[defaultValueIndex].isSelect = true;
            setListHairColorOptions(listHairColorsCurrent);
        }
    }, [defaultValueHairColor, defaultValueHairColor?.value, listHairColors, loadingQuiz]);

    const handleSelectItem = (item) => {
        setAnswerUpdated(true);
        if (Boolean(!userName)) {
            setListHairColorOptions(
                listHairColors.map((d) => {
                    if (item.id === d.id) {
                        d.isSelect = true;
                    } else {
                        d.isSelect = false;
                    }
                    return d;
                }),
            );
        }
    };

    const input = {
        questionId: EQuestionId.HAIR_COLOR,
        questionSubject: ESubjectQuestion.HAIR_COLOR,
        contextType: quizContextType,
        contextId: EQuizId.HAIR_COLOR,
        valueType: 'STRING',
    };

    const handleClickAnswer = () => {
        const currentItemSelected = listHairColorOptions.find((item) => item.isSelect);
        if (Boolean(!userName) && currentItemSelected) {
            createAnswer({ ...input, value: currentItemSelected.value });
        }
    };

    const handleSkip = async () => {
        setSkipping(true);
        if (Boolean(!userName)) {
            await createAnswer({ ...input, value: '', isSkipped: true });
        }
        setSkipping(false);
    };

    const valueHairColor = useMemo(() => listHairColorOptions?.find((d) => d.isSelect), [listHairColorOptions]);

    const isValidAnswer = useValidateAnswers(
        isUpdate,
        listHairColorOptions?.find((d) => d.isSelect)?.value,
        answerAlreadyUpdated,
    );

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.HAIR_COLOR}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={handleClickAnswer}
            isUpdate={location.pathname.includes(PATH.MY_STYLE_PREFERENCES)}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            handleSkipQuestion={handleSkip}
            loading={loadingAnswer && !isSkipping}
            loadingSkip={loadingAnswer && isSkipping}
            containerLoading={loading || loadingQuiz || userLoading}
            nextBtnDisabled={!isValidAnswer}
        >
            <Box textAlign={'center'} display={'flex'} flexDirection={'column'} width={'100%'} height={'100%'}>
                <Box className={classes.quizRowContainer}>
                    {Boolean(!user) ? (
                        <ScrollContainer className={classes.quizRow}>
                            {listHairColorOptions.length > 0 &&
                                !loading &&
                                listHairColorOptions?.map((item, index) => (
                                    <HairColorItem item={item} index={index} handleSelectItem={handleSelectItem} />
                                ))}
                        </ScrollContainer>
                    ) : valueHairColor ? (
                        <Box
                            width={'40%'}
                            margin="10px auto 0"
                            borderRadius={8}
                            overflow={'hidden'}
                            className={classes.wrapperItem}
                        >
                            <Box className={classes.colorItem}>
                                <img
                                    style={{ display: 'inline-block', width: '100%  ' }}
                                    src={valueHairColor.imageUrl}
                                    alt={valueHairColor.title}
                                />
                            </Box>
                            <p className={classes.itemTitle}>{valueHairColor.title}</p>
                        </Box>
                    ) : (
                        <EmptyAnswer title={'Hair Color'} userName={user?.firstName} />
                    )}
                </Box>
            </Box>
        </CustomizeLayoutQuiz>
    );
};
export default HairColor;
